// src/components/calendar/CalendarViews/WeekViewShadcn.jsx - FIXED: Simplified with proper event positioning
import React from 'react';
import { Spinner } from '../../ui/spinner';

// ✅ KEEP: Import our shadcn week components
import {
  WeekViewHeaderShadcn,
  WeekViewTimeRowShadcn,
  WeekViewEventCardShadcn
} from '../../ui/calendar/week';

const WeekViewShadcn = ({
  currentDate,
  previewEvent,
  onTimeSlotClick,
  onEventClick,
  isAdmin,
  showEventPanel,
  partnerMode = false,

  // Accept events data as props instead of useEvents hook
  events = [],      // Renamed from calendarEvents for clarity
  loading = false,  // Loading state from parent
  error = null,      // Error state from parent

  // creation mode props
  isInCreationMode = false,

  availabilityDates,
  viewingPartnerAvailability,

  // Time range configuration
  timeRange = { start: 8, end: 18 }  // Default: 8 AM - 6 PM
}) => {

  // Business hours configuration - now from props
  const BUSINESS_HOURS = {
    start: timeRange.start,
    end: timeRange.end,
  };

  // Week dates calculation - FIXED to create clean midnight dates
  const getWeekDates = (date) => {
    // Get the year, month, day parts WITHOUT time component
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();

    // Create a clean date at midnight
    const cleanDate = new Date(year, month, day);
    const dayOfWeek = cleanDate.getDay();

    // Calculate start of week (Sunday)
    const startOfWeek = new Date(year, month, day - dayOfWeek);

    const dates = [];
    for (let i = 0; i < 7; i++) {
      // Create each date as a clean midnight date
      const weekDate = new Date(year, month, day - dayOfWeek + i);
      dates.push(weekDate);
    }
    return dates;
  };

  // Use passed events instead of hook method
  const getEventsForDate = (dateStr) => {
    return events.filter(event => {
      if (!event.start) return false;
      const eventDateStr = event.start.toDateString()
      return eventDateStr === dateStr;
    });
  };

  // Use passed events instead of hook method
  const getAllEventsForDate = (date) => {
    const dateStr = date.toDateString()
    return getEventsForDate(dateStr);
  };

  // Get events for specific time slot (CRITICAL FUNCTION)
  const getEventsForTimeSlot = (date, hour) => {
    const allEvents = getAllEventsForDate(date);

    return allEvents.filter(event => {
      if (!event.start) return false;

      const eventStart = new Date(event.start);
      const eventDate = eventStart.toDateString();
      const slotDate = date.toDateString();
      const eventStartHour = eventStart.getHours();

      // Only return events that START in this specific hour on this date
      return eventDate === slotDate && eventStartHour === hour;
    });
  };

  // Get background events for time slot (CRITICAL FUNCTION)
  const getBackgroundEventsForTimeSlot = (date, hour) => {
    const timeSlotEvents = getEventsForTimeSlot(date, hour);
    return timeSlotEvents.filter(event => event.isBackground || event.is_blocking);
  };

  // ✅ CHANGE 1: Simplified event layout - no complex z-index calculations
  const getSimpleEventLayout = (events) => {
    if (events.length === 0) return [];

    return events.map((event, index) => ({
      ...event,
      isMainEvent: index === 0,
      isPeekEvent: index > 0,
      peekIndex: index
    }));
  };

  // ✅ CHANGE 2: Fixed event positioning - handles multi-hour events properly
  const getEventStyle = (event) => {
    const startHour = event.start.getHours();
    const startMinutes = event.start.getMinutes();
    const endHour = event.end.getHours();
    const endMinutes = event.end.getMinutes();

    // Calculate position within the CURRENT hour slot only
    const minutesFromHourStart = startMinutes;
    const topOffset = (minutesFromHourStart / 60) * 100;

    // Calculate total duration in hours
    const startTime = startHour + startMinutes / 60;
    const endTime = endHour + endMinutes / 60;
    const durationInHours = endTime - startTime;

    // Height spans multiple hour slots if needed
    const height = durationInHours * 100;

    return {
      top: `${topOffset}%`,
      height: `${height}%`
    };
  };

  // Quick action handler (IMPORTANT FUNCTION)
  const handleQuickAction = (event, action) => {
    console.log('Quick action:', action, 'on event:', event.id);
    // TODO: Implement quick action handlers
  };

  const weekDates = getWeekDates(currentDate);
  const hours = Array.from({ length: BUSINESS_HOURS.end - BUSINESS_HOURS.start }, (_, i) => BUSINESS_HOURS.start + i);

  // Use passed loading state instead of hook
  if (loading && !events.length) {
    return (
      <div className="week-view flex flex-col h-full">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Spinner className="size-8 text-blue-500 mx-auto mb-2" />
            <p className="text-slate-400 text-sm">Loading calendar...</p>
          </div>
        </div>
      </div>
    );
  }

  // Use passed error state instead of hook
  if (error) {
    return (
      <div className="week-view flex flex-col h-full">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-red-400 text-sm">Error loading calendar: {error}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-2 px-3 py-1 bg-slate-700 text-slate-300 rounded text-sm hover:bg-slate-600"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Complete JSX using shadcn components with all original functionality
  return (
    <div className="week-view flex flex-col h-full">
      {/* Week header using shadcn component */}
      <WeekViewHeaderShadcn
        weekDates={weekDates}
        currentDate={currentDate}
      />

      {/* Week grid using shadcn time row components with simplified functionality */}
      <div className="flex-1 overflow-auto">
        {hours.map(hour => (
          <WeekViewTimeRowShadcn
            key={hour}
            hour={hour}
            weekDates={weekDates}
            onTimeSlotClick={onTimeSlotClick}
            getEventsForTimeSlot={getEventsForTimeSlot}
            getBackgroundEventsForTimeSlot={getBackgroundEventsForTimeSlot}
            isAdmin={isAdmin}
            availabilityDates={availabilityDates}
            viewingPartnerAvailability={viewingPartnerAvailability}
          >
            {/* ✅ CHANGE 3: Simplified event rendering - main event + simple peek slivers */}
            {(date, hour, dayIndex) => {
              const timeSlotEvents = getEventsForTimeSlot(date, hour);

              if (timeSlotEvents.length === 0) return null;

              // Apply simple layout calculation
              const eventsWithLayout = getSimpleEventLayout(timeSlotEvents);

              return eventsWithLayout.map((event, eventIndex) => {
                const eventStyle = getEventStyle(event);

                if (event.isMainEvent) {
                  // Main event - full card using shadcn component
                  return (
                    <div
                      key={event.id}
                      className="absolute inset-1"
                      style={eventStyle}
                    >
                      <WeekViewEventCardShadcn
                        event={event}
                        onClick={onEventClick}
                        showEventPanel={showEventPanel}
                      />
                    </div>
                  );
                } else if (event.isPeekEvent) {
                  // Peek event - glassmorphism sliver
                  const peekWidth = 28;
                  const peekLeft = (event.peekIndex - 1) * (peekWidth + 4);

                  return (
                    <div
                      key={event.id}
                      className="absolute top-1 h-14 rounded-lg cursor-pointer overflow-hidden transition-all duration-300 hover:scale-105 border border-slate-700/50 hover:border-slate-600/50 bg-slate-800/40 backdrop-blur-sm"
                      style={{
                        left: `${peekLeft + 2}px`,
                        width: `${peekWidth}px`,
                        zIndex: 20
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!showEventPanel) onEventClick(event);
                      }}
                      title={`${event.title} - ${event.start.toLocaleTimeString('en-US', {
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                      })}`}
                    >
                      {/* Glassmorphism gradient overlay */}
                      <div className="absolute inset-0 bg-gradient-to-b from-white/5 to-transparent pointer-events-none" />

                      {/* Colored accent stripe */}
                      <div
                        className="absolute top-0 left-0 w-1 h-full"
                        style={{ backgroundColor: event.backgroundColor }}
                      />
                    </div>
                  );
                }

                return null;
              });
            }}
          </WeekViewTimeRowShadcn>
        ))}
      </div>
    </div>
  );
};

export default WeekViewShadcn;